'use client';

import React from 'react';
import { Search, SlidersHorizontal, UserPlus, Download, Upload, X } from 'lucide-react';
import { FilterState } from './ContactsListContent';

interface Props {
  total: number;
  filtered: number;
  filterOpen: boolean;
  onToggleFilter: () => void;
  filters: FilterState;
  onSearchChange: (v: string) => void;
}

export default function ContactsHeader({
  total, filtered, filterOpen, onToggleFilter, filters, onSearchChange
}: Props) {
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== 'search' && v !== '').length;

  return (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 py-4">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Contacts</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="font-mono font-semibold text-foreground">{filtered}</span>
            {filtered !== total && ` of ${total}`} contacts
            {filtered !== total && <span className="text-primary ml-1">· filtered</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs">
            <Upload size={14} />
            Import CSV
          </button>
          <button className="btn-ghost text-xs">
            <Download size={14} />
            Export
          </button>
          <button className="btn-primary text-xs">
            <UserPlus size={14} />
            Add Contact
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, company, email, title…"
            value={filters.search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="input-base pl-9 pr-8 text-xs h-9"
          />
          {filters.search && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={12} />
            </button>
          )}
        </div>
        {/* Filter toggle */}
        <button
          onClick={onToggleFilter}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors duration-150 ${
            filterOpen
              ? 'bg-primary/10 text-primary border-primary/30' :'bg-white text-muted-foreground border-border hover:bg-muted'
          }`}
        >
          <SlidersHorizontal size={13} />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-0.5 w-4 h-4 rounded-full bg-primary text-white text-[9px] flex items-center justify-center font-mono">
              {activeFilterCount}
            </span>
          )}
        </button>
        {/* Saved views */}
        <div className="flex items-center gap-1">
          {[
            { id: 'sv-priority', label: 'Top Priority' },
            { id: 'sv-linkedin', label: 'LinkedIn Follow-up' },
            { id: 'sv-meeting', label: 'Mtg Pending' },
            { id: 'sv-overdue', label: 'Overdue' },
          ].map((view) => (
            <button
              key={view.id}
              className="px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted hover:text-foreground transition-colors"
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}